import { revitBridge, type ExpectedRevitDocumentIdentity } from '../../api/revit/revitBridge';
import type { RevitOperationProgressSnapshot } from '../../api/grpc/operationProgressClient';
import { buildParameterAdditionPayload } from '../../lib/revit/parameterAdditionPayload';
import type { ConnectorParameterRestoreEntry } from '../../types';
import type {
  BoltDefaultParameterRemoval,
  BoltFamilyParameterBindingChange,
  BoltModelingType,
} from './planner';
import type { BoltParameterTypeHint } from './boltParameterTypes';

export interface BoltFamilyHostOptions {
  expectedDocumentIdentity?: ExpectedRevitDocumentIdentity;
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
}

export interface BoltFamilyParameterHostResult {
  success: boolean;
  error?: string;
  message?: string;
  modifiedFamiliesCount?: number;
  resolvedFamilyTargetCount?: number;
  processedFamilyTargetCount?: number;
  openedFamilyCount?: number;
  unmodifiedFamilyTargetCount?: number;
  existingFamilyParameterCount?: number;
  conflictCount?: number;
  failureCount?: number;
}

function toBoltModelingType(targetObjectType: string | undefined): BoltModelingType {
  return targetObjectType === 'GASKET' ? 'GASKET' : 'BOLT';
}

export async function addParametersToBoltFamilies(input: {
  familyIds: string[];
  targetObjectType: BoltModelingType;
  parameterNames: string[];
  bindingKind: 'type' | 'instance';
  parameterTypeHints?: BoltParameterTypeHint[];
  options?: BoltFamilyHostOptions;
}): Promise<BoltFamilyParameterHostResult> {
  // Always send the object-type filter alongside explicit ids: when an id from
  // the snapshot no longer resolves in the current document, the backend falls
  // back to the live object-type/category resolution instead of failing.
  const payload = buildParameterAdditionPayload(input.parameterNames, {
    targetObjectTypes: [input.targetObjectType],
    bindingKind: input.bindingKind,
  });

  const result = await revitBridge.addParametersToSpecificFamilies(
    input.familyIds.filter(Boolean),
    input.parameterNames,
    false,
    {
      categories: payload.categories,
      sharedParameterFilePath: payload.sharedParameterFilePath,
      sharedParameterFileContent: payload.sharedParameterFileContent,
      bindingKind: payload.bindingKind,
      targetObjectTypes: payload.targetObjectTypes,
      parameterTypeHints: input.parameterTypeHints,
      ...input.options?.expectedDocumentIdentity,
      onProgress: input.options?.onProgress,
    }
  );

  const conflictCount = result.data?.conflictCount ?? 0;
  const failureCount = result.data?.failureCount ?? 0;
  const hasHostIssues = conflictCount > 0 || failureCount > 0;

  return {
    success: result.success && !hasHostIssues,
    error: result.error || (hasHostIssues ? result.data?.message : undefined),
    message: result.data?.message,
    modifiedFamiliesCount: result.data?.modifiedFamiliesCount,
    resolvedFamilyTargetCount: result.data?.resolvedFamilyTargetCount,
    processedFamilyTargetCount: result.data?.processedFamilyTargetCount,
    openedFamilyCount: result.data?.openedFamilyCount,
    unmodifiedFamilyTargetCount: result.data?.unmodifiedFamilyTargetCount,
    existingFamilyParameterCount: result.data?.existingFamilyParameterCount,
    conflictCount,
    failureCount,
  };
}

export async function removeBoltFamilyParameters(input: {
  familyId: string;
  familyName: string;
  parametersToRemove: BoltDefaultParameterRemoval[];
  options?: BoltFamilyHostOptions;
}): Promise<{ success: boolean; error?: string }> {
  if (input.parametersToRemove.length === 0) {
    return { success: true };
  }

  const result = await revitBridge.undoAddedParametersToSpecificFamilies({
    entries: input.parametersToRemove.map((parameter) => ({
      familyId: input.familyId,
      familyName: input.familyName,
      parameterName: parameter.parameterName,
      existedBefore: false,
    })),
    ...input.options?.expectedDocumentIdentity,
  });

  return {
    success: result.success,
    error: result.error,
  };
}

export async function rebindBoltFamilyParameters(input: {
  familyId: string;
  familyName: string;
  parametersToRebind: BoltFamilyParameterBindingChange[];
  options?: BoltFamilyHostOptions;
}): Promise<{ success: boolean; error?: string }> {
  if (input.parametersToRebind.length === 0) {
    return { success: true };
  }

  const result = await revitBridge.undoAddedParametersToSpecificFamilies({
    entries: input.parametersToRebind.map((parameter) => ({
      familyId: input.familyId,
      familyName: input.familyName,
      parameterName: parameter.parameterName,
      existedBefore: true,
      previousBindingKind: parameter.bindingKind,
    })),
    ...input.options?.expectedDocumentIdentity,
  });

  return {
    success: result.success,
    error: result.error,
  };
}

export async function restoreRemovedBoltFamilyParameters(input: {
  entries: ConnectorParameterRestoreEntry[];
  expectedDocumentIdentity?: ExpectedRevitDocumentIdentity;
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
}): Promise<{ success: boolean; error?: string }> {
  const groupedEntries = input.entries.reduce((groups, entry) => {
    const key = [
      entry.familyId,
      entry.familyName,
      entry.bindingKind,
      entry.targetObjectType ?? '',
    ].join('||');
    const bucket = groups.get(key) ?? [];
    bucket.push(entry);
    groups.set(key, bucket);
    return groups;
  }, new Map<string, ConnectorParameterRestoreEntry[]>());

  for (const restoreEntries of groupedEntries.values()) {
    const firstEntry = restoreEntries[0];
    if (!firstEntry) {
      continue;
    }

    const result = await addParametersToBoltFamilies({
      familyIds: [firstEntry.familyId],
      targetObjectType: toBoltModelingType(firstEntry.targetObjectType),
      parameterNames: restoreEntries.map((entry) => entry.parameterName),
      bindingKind: firstEntry.bindingKind,
      options: {
        expectedDocumentIdentity: input.expectedDocumentIdentity,
        onProgress: input.onProgress,
      },
    });

    if (!result.success) {
      return {
        success: false,
        error:
          result.error ||
          `Failed to restore removed bolt family parameters on ${firstEntry.familyName}.`,
      };
    }
  }

  return { success: true };
}
